import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { createLogger } from '../../../shared/config/logger';
import { extractErrorMessage } from '../../../shared/utils/errorHandling';
import { redactSensitive } from '../../../shared/utils/logSanitization';
import { validateEmail, validatePassword, validateName } from '../../../shared/utils/validators';
import { PrivacyPolicyModal } from '../../../shared/components/PrivacyPolicyModal';
import { TermsAndConditionsModal } from '../../../shared/components/TermsAndConditionsModal';
const logger = createLogger('Register.jsx');
/**
 * Register Component
 *
 * This component handles the merchant registration process for the LoyalPass platform.
 * It provides a form for collecting merchant information and creates a new user account
 * in Supabase authentication system.
 *
 * The registration flow:
 * 1. User fills out the registration form with merchant details
 * 2. On submit, the component creates a new user in Supabase Auth
 * 3. User metadata (name, phone, role) is stored in the Auth user's metadata
 * 4. Upon successful registration, the user is redirected to the home page
 * 5. Email verification may be required depending on Supabase configuration
 */
export default function Register() {
    // State variables to manage form inputs and component state
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        confirmPassword: '',
        business_name: '',
    });
    const [state, setState] = useState({ error: null, loading: false, message: null });
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [acceptedTerms, setAcceptedTerms] = useState(false);
    const [showPrivacyModal, setShowPrivacyModal] = useState(false);
    const [showTermsModal, setShowTermsModal] = useState(false);
    const { register } = useAuth();

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    // Hook for programmatic navigation
    const navigate = useNavigate();

    const validateForm = (formData) => {
        const { email, password, confirmPassword, business_name } = formData;

        const emailValidation = validateEmail(email);
        if (!emailValidation.valid) {
            return emailValidation.error;
        }

        const passwordValidation = validatePassword(password);
        if (!passwordValidation.valid) {
            return passwordValidation.error;
        }

        if (password !== confirmPassword) {
            return 'Passwords do not match';
        }

        const businessNameValidation = validateName(business_name);
        if (!businessNameValidation.valid) {
            return businessNameValidation.error;
        }

        // No errors found
        return null;
    };

    /**
     * Form submission handler
     *
     * This function processes the registration form submission:
     * - Prevents default form submission behavior
     * - Sets loading state and clears previous errors
     * - Attempts to create a new user account with Supabase
     * - Stores user metadata (name, phone, role) //!!! In this register portal we are setting the role to merchant
     *                                            //!!! In other portals, set the role appropriately, eg. customer
     * - Handles success and error cases
     *
     * @param {Event} e - The form submission event
     */
    const handleSubmit = async (e) => {
        e.preventDefault();
        setState((prevState) => ({
            ...prevState,
            error: null,
            loading: true,
            message: 'Form submitted. Please wait...',
        }));

        const errorMessage = validateForm(formData);
        if (errorMessage) {
            setState((prevState) => ({ ...prevState, error: errorMessage, loading: false }));
            return;
        }

        if (!acceptedTerms) {
            setState((prevState) => ({ 
                ...prevState, 
                error: 'You must accept the Privacy Policy and Terms and Conditions to create an account.', 
                loading: false 
            }));
            return;
        }

        setState((prevState) => ({
            ...prevState,
            error: null,
            message: 'Sign up process initiated...',
        }));

        logger.info('Register form submitted', redactSensitive(formData));
        try {
            const result = await register(formData); // Now routes through backend for atomic registration
            logger.info('User registered successfully via backend', { userId: result?.user?.id });

            setState({
                error: null,
                loading: false,
                message: 'Registration successful! Please check your inbox for a verification email.',
            }); // Update state to reflect success
        } catch (error) {
            const errorMessage = extractErrorMessage(error);
            logger.error('Error in register form submission', { error: errorMessage });
            setState((prevState) => ({
                ...prevState,
                error: errorMessage,
                loading: false,
                message: null,
            }));
        }
    };

    /**
     * Component Render
     *
     * Renders a responsive registration form with the following elements:
     * - Form title
     * - Message display (when applicable)
     * - Input fields for merchant name, email, phone, and password
     * - Submit button with loading state
     * - Alternative login button
     */
    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-100 px-4">
            <form
                onSubmit={handleSubmit}
                className="w-full max-w-md bg-white p-8 rounded-2xl shadow-lg space-y-6"
            >
                <h1 className="text-2xl font-bold text-cta text-center">
                    Loyal<span className="text-secondary">Pass</span>
                </h1>
                <h2 className="text-xl font-semibold text-center text-gray-800">
                    Merchant Registration
                </h2>

                {/* Message and error display */}
                {state.error && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                        {state.error}
                    </div>
                )}
                {state.message && !state.error && (
                    <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded">
                        {state.message}
                    </div>
                )}

                {/* Merchant email input field - used for authentication */}
                <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Merchant email"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3498db] transition"
                    required
                />

                {/* Password input field with toggle */}
                <div className="relative">
                    <input
                        type={showPassword ? 'text' : 'password'}
                        name="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        placeholder="Password"
                        className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3498db] transition"
                        required
                        minLength="6"
                    />
                    <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                        disabled={state.loading}
                    >
                        {showPassword ? (
                            <svg
                                className="h-5 w-5"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21"
                                />
                            </svg>
                        ) : (
                            <svg
                                className="h-5 w-5"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                />
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                />
                            </svg>
                        )}
                    </button>
                </div>

                {/* Confirm password input field with toggle */}
                <div className="relative">
                    <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        name="confirmPassword"
                        value={formData.confirmPassword || ''}
                        onChange={handleInputChange}
                        placeholder="Confirm Password"
                        className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3498db] transition"
                        required
                    />
                    <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                        disabled={state.loading}
                    >
                        {showConfirmPassword ? (
                            <svg
                                className="h-5 w-5"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21"
                                />
                            </svg>
                        ) : (
                            <svg
                                className="h-5 w-5"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                />
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                />
                            </svg>
                        )}
                    </button>
                </div>

                {/* Merchant name input field */}
                <input
                    type="text"
                    name="business_name"
                    value={formData.business_name}
                    onChange={handleInputChange}
                    placeholder="Business Name"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3498db] transition"
                    required
                />

                {/* Privacy Policy and Terms and Conditions checkbox */}
                <div className="flex items-start">
                    <input
                        type="checkbox"
                        id="acceptTerms"
                        checked={acceptedTerms}
                        onChange={(e) => setAcceptedTerms(e.target.checked)}
                        className="mt-1 mr-2 h-4 w-4 text-[#3498db] border-gray-300 rounded focus:ring-[#3498db]"
                        required
                    />
                    <label htmlFor="acceptTerms" className="text-sm text-gray-700">
                        I accept the{' '}
                        <button
                            type="button"
                            onClick={(e) => {
                                e.preventDefault();
                                setShowPrivacyModal(true);
                            }}
                            className="text-[#3498db] hover:underline focus:outline-none"
                        >
                            Privacy Policy
                        </button>
                        {' '}and{' '}
                        <button
                            type="button"
                            onClick={(e) => {
                                e.preventDefault();
                                setShowTermsModal(true);
                            }}
                            className="text-[#3498db] hover:underline focus:outline-none"
                        >
                            Terms and Conditions
                        </button>
                    </label>
                </div>

                {/* Submit button - shows loading state during form submission */}
                <button
                    type="submit"
                    className="w-full bg-card text-white py-3 rounded-lg hover:bg-primary transition font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={state.loading || !acceptedTerms}
                >
                    {state.loading ? 'Signing up...' : 'Sign up'}
                </button>

                {/* Alternative navigation button to login page */}
                <button
                    type="button"
                    onClick={() => navigate('/login')}
                    className="w-full bg-gray-200 text-gray-800 py-3 rounded-lg hover:bg-gray-300 transition"
                    disabled={state.loading}
                >
                    Login Instead
                </button>
            </form>

            {/* Privacy Policy Modal */}
            <PrivacyPolicyModal 
                isOpen={showPrivacyModal} 
                onClose={() => setShowPrivacyModal(false)} 
            />

            {/* Terms and Conditions Modal */}
            <TermsAndConditionsModal 
                isOpen={showTermsModal} 
                onClose={() => setShowTermsModal(false)} 
            />
        </div>
    );
}
